import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './ReportImage.css';
import logo from "../../assets/fishtagged-logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Hamburger from '../components/Hamburger';
import { loadImages } from "../../imageDB";
import {
  faAngleLeft, faAngleRight, faCamera, faCalendarDays, faClock,
  faLocationDot, faExclamationTriangle, faTimes, faCheck, faCircleQuestion,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import '../../App.css';
import '../../screens/components/Hamburger.css';
import * as exifr from "exifr";
import { useFileContext } from "../../FileContext";
import { snapshotCurrentReport, upsertReport } from '../../reportStorage';

function savedRI() {
  try { return JSON.parse(sessionStorage.getItem("reportImageData")) || {}; } catch { return {}; }
}

function MapModal({ initialLat, initialLng, onConfirm, onClose }) {
  const mapRef    = useRef(null);
  const mapObjRef = useRef(null);
  const markerRef = useRef(null);
  const FALLBACK_LAT = 39.060886;
  const FALLBACK_LNG = -77.328595;
  const [pinLat, setPinLat] = useState(initialLat ?? FALLBACK_LAT);
  const [pinLng, setPinLng] = useState(initialLng ?? FALLBACK_LNG);
  const geoFiredRef = useRef(false);

  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css'; link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    const initMap = () => {
      const L = window.L;
      const startLat = initialLat ?? FALLBACK_LAT;
      const startLng = initialLng ?? FALLBACK_LNG;
      if (mapRef.current._leaflet_id) mapRef.current._leaflet_id = null;
      const map = L.map(mapRef.current).setView([startLat, startLng], initialLat ? 13 : 5);
      mapObjRef.current = map;
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap contributors' }).addTo(map);
      const icon = L.icon({
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        iconSize: [25, 41], iconAnchor: [12, 41],
      });
      const marker = L.marker([startLat, startLng], { draggable: true, icon }).addTo(map);
      markerRef.current = marker;
      marker.on('dragend', () => {
        geoFiredRef.current = true;
        const pos = marker.getLatLng();
        setPinLat(parseFloat(pos.lat.toFixed(6)));
        setPinLng(parseFloat(pos.lng.toFixed(6)));
      });
      map.on('click', (e) => {
        geoFiredRef.current = true;
        marker.setLatLng(e.latlng);
        setPinLat(parseFloat(e.latlng.lat.toFixed(6)));
        setPinLng(parseFloat(e.latlng.lng.toFixed(6)));
      });
      setTimeout(() => map.invalidateSize(), 100);

      if (!initialLat && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (geoFiredRef.current) return;
            const lat = parseFloat(pos.coords.latitude.toFixed(6));
            const lng = parseFloat(pos.coords.longitude.toFixed(6));
            if (mapObjRef.current && markerRef.current) {
              markerRef.current.setLatLng([lat, lng]);
              mapObjRef.current.setView([lat, lng], 13);
            }
            setPinLat(lat);
            setPinLng(lng);
          },
          () => {},
          { timeout: 8000, maximumAge: 60000 }
        );
      }
    };

    if (window.L) initMap();
    else {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = initMap;
      document.head.appendChild(script);
    }
    return () => { if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; } };
  }, []);

  return (
    <div className="ri-modal-overlay">
      <div className="ri-map-modal">
        <button className="ri-modal-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
        <h2 className="ri-modal-title">Pin Your Location</h2>
        <p className="ri-map-hint">Tap the map or drag the pin to set your location.</p>
        <div ref={mapRef} className="ri-map-container" />
        <div className="ri-map-coords"><span>Lat: {pinLat}</span><span>Lng: {pinLng}</span></div>
        <div className="ri-modal-actions">
          <button className="ri-modal-cancel" onClick={onClose}>Cancel</button>
          <button className="ri-modal-choose" onClick={() => onConfirm(pinLat, pinLng)}>Confirm Location</button>
        </div>
      </div>
    </div>
  );
}

export default function ReportImage({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getRawFile } = useFileContext();

  const [showImageModal, setShowImageModal] = useState(false);
  const [images, setImages]               = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [pendingImage, setPendingImage]   = useState(null);

  const [dateTaken,  setDateTaken]  = useState(() => savedRI().dateTaken  || "");
  const [timeTaken,  setTimeTaken]  = useState(() => savedRI().timeTaken  || "");
  const [latitude,   setLatitude]   = useState(() => savedRI().latitude   || "");
  const [longitude,  setLongitude]  = useState(() => savedRI().longitude  || "");
  const [landmark,   setLandmark]   = useState(() => savedRI().landmark   || "");
  const [noLocation, setNoLocation] = useState(() => savedRI().noLocation || false);

  const [editingDate,   setEditingDate]   = useState(false);
  const [editingTime,   setEditingTime]   = useState(false);
  const [editingCoords, setEditingCoords] = useState(false);

  const [latError, setLatError] = useState("");
  const [lngError, setLngError] = useState("");

  const [showMapModal,        setShowMapModal]        = useState(false);
  const [missingImages,       setMissingImages]       = useState(false);
  const [selectionError,      setSelectionError]      = useState(false);
  const [exifPopup,           setExifPopup]           = useState(false);
  const [exifPopupMessage,    setExifPopupMessage]    = useState("");
  const [exifPopupType,       setExifPopupType]       = useState("error");
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [validationError,     setValidationError]     = useState("");
  const userEditedRef = useRef(false);
  const lastExtractedIdRef = useRef(null);

  const markEdited = () => { userEditedRef.current = true; };

  const handleLatChange = (val) => {
    setLatitude(val);
    if (val === "") { setLatError(""); return; }
    const n = parseFloat(val);
    setLatError(isNaN(n) || n < -90 || n > 90 ? "Latitude must be between -90 and 90" : "");
  };

  const handleLngChange = (val) => {
    setLongitude(val);
    if (val === "") { setLngError(""); return; }
    const n = parseFloat(val);
    setLngError(isNaN(n) || n < -180 || n > 180 ? "Longitude must be between -180 and 180" : "");
  };

  useEffect(() => {
    const saved = sessionStorage.getItem("reportImageData");
    if (saved) {
      try {
        const d = JSON.parse(saved);
        if (d.dateTaken)  setDateTaken(d.dateTaken);
        if (d.timeTaken)  setTimeTaken(d.timeTaken);
        if (d.latitude)   setLatitude(d.latitude);
        if (d.longitude)  setLongitude(d.longitude);
        if (d.landmark)   setLandmark(d.landmark);
        if (d.noLocation) setNoLocation(d.noLocation);
        if (d.userEdited) userEditedRef.current = true;
      } catch {}
    }
  }, []);

  useEffect(() => {
    loadImages().then((parsed) => {
      if (!parsed || !parsed.length) { setMissingImages(true); setImages([]); setSelectedImage(null); return; }
      setMissingImages(false);
      setImages(parsed);
      setSelectedImage((prev) => {
        if (prev && parsed.find((img) => img.id === prev.id)) return prev;
        const savedId = savedRI().selectedImageId;
        const match = savedId && parsed.find((img) => img.id === savedId);
        return match || parsed[0];
      });
    });
  }, [location]);

  // Auto-save on every field change.
  // Note: selectedImagePreview is intentionally excluded — base64 image data
  // must never be written to sessionStorage as it flows into localStorage via
  // snapshotCurrentReport and causes QuotaExceededError.
  useEffect(() => {
    const data = {
      selectedImageId:   selectedImage?.id   ?? "",
      selectedImageName: selectedImage?.name ?? "",
      dateTaken,
      timeTaken,
      latitude: noLocation ? "" : latitude,
      longitude: noLocation ? "" : longitude,
      landmark,
      noLocation,
      userEdited: userEditedRef.current,
    };
    sessionStorage.setItem("reportImageData", JSON.stringify(data));

    const activeId = sessionStorage.getItem("activeReportId") || undefined;
    loadImages().then((imgs) => {
      const report = snapshotCurrentReport(activeId, imgs || []);
      upsertReport(report);
      if (!sessionStorage.getItem("activeReportId")) {
        sessionStorage.setItem("activeReportId", report.id);
      }
    });
  }, [selectedImage, dateTaken, timeTaken, latitude, longitude, landmark, noLocation]);

  const extractExif = async (image) => {
    if (!image) return;

    const isSameImage = lastExtractedIdRef.current === image.id;
    if (isSameImage && userEditedRef.current) return;

    lastExtractedIdRef.current = image.id;

    if (isSameImage) {
      const saved = sessionStorage.getItem("reportImageData");
      if (saved) {
        try {
          const d = JSON.parse(saved);
          if (d.selectedImageId === image.id) return;
        } catch {}
      }
    }

    setDateTaken(""); setTimeTaken(""); setLatitude(""); setLongitude("");
    setLatError(""); setLngError("");
    setEditingDate(false); setEditingTime(false); setEditingCoords(false);

    const cameraLocation = image.cameraLocation ?? null;
    const cameraDatetime = image.cameraDatetime ?? null;
    const isCameraPhoto  = image.isCamera === true;

    const showExifResult = (gotDate, gotTime, gotLocation) => {
      const pulled = [];
      const missing = [];
      if (gotDate)     pulled.push("date");     else missing.push("date");
      if (gotTime)     pulled.push("time");     else missing.push("time");
      if (gotLocation) pulled.push("location"); else missing.push("location");

      if (missing.length === 0) {
        setExifPopupType("success");
        setExifPopupMessage("TagApp was able to fill this page out using metadata in your images:\n✓ Date\n✓ Time\n✓ Location");
      } else if (pulled.length > 0) {
        setExifPopupType("partial");
        const pulledStr  = pulled.map(f => `✓ ${f.charAt(0).toUpperCase() + f.slice(1)}`).join("\n");
        const missingStr = missing.map(f => `✗ ${f.charAt(0).toUpperCase() + f.slice(1)}`).join("\n");
        setExifPopupMessage(`TagApp was able to detect the following fields in your images' metadata:\n\n${pulledStr}\n\nPlease fill out the following fields manually: \n\n${missingStr}`);
      } else {
        setExifPopupType("error");
        setExifPopupMessage("TagApp could not detect a date, time or location in your images' metadata. Please fill out those fields manually.");
      }
      setExifPopup(true);
    };

    try {
      const rawFile = getRawFile(image.id);

      if (!rawFile) {
        const hasLocation = !!cameraLocation;
        const hasDatetime = isCameraPhoto && !!cameraDatetime;
        if (hasLocation) { setLatitude(cameraLocation.latitude); setLongitude(cameraLocation.longitude); }
        else setEditingCoords(true);
        if (hasDatetime) { setDateTaken(cameraDatetime.date ?? ""); setTimeTaken(cameraDatetime.time ?? ""); }
        else { setEditingDate(true); setEditingTime(true); }
        showExifResult(hasDatetime, hasDatetime, hasLocation);
        return;
      }

      const data = await exifr.parse(rawFile, { gps: true });

      if (!data) {
        const hasLocation = !!cameraLocation;
        const hasDatetime = isCameraPhoto && !!cameraDatetime;
        if (hasLocation) { setLatitude(cameraLocation.latitude); setLongitude(cameraLocation.longitude); }
        else setEditingCoords(true);
        if (hasDatetime) { setDateTaken(cameraDatetime.date ?? ""); setTimeTaken(cameraDatetime.time ?? ""); }
        else { setEditingDate(true); setEditingTime(true); }
        showExifResult(hasDatetime, hasDatetime, hasLocation);
        return;
      }

      let gotDate = false, gotTime = false, gotLocation = false;

      if (data.DateTimeOriginal) {
        const d = new Date(data.DateTimeOriginal);
        setDateTaken(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
        setTimeTaken(`${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`);
        gotDate = true; gotTime = true;
      } else if (isCameraPhoto && cameraDatetime) {
        setDateTaken(cameraDatetime.date ?? "");
        setTimeTaken(cameraDatetime.time ?? "");
        gotDate = true; gotTime = true;
      } else {
        setEditingDate(true); setEditingTime(true);
      }

      if (data.latitude && data.longitude) {
        setLatitude(data.latitude.toFixed(6)); setLongitude(data.longitude.toFixed(6));
        gotLocation = true;
      } else if (cameraLocation) {
        setLatitude(cameraLocation.latitude); setLongitude(cameraLocation.longitude);
        gotLocation = true;
      } else {
        setEditingCoords(true);
      }

      showExifResult(gotDate, gotTime, gotLocation);

    } catch {
      setEditingDate(true); setEditingTime(true); setEditingCoords(true);
      showExifResult(false, false, false);
    }
  };

  useEffect(() => { if (selectedImage) extractExif(selectedImage); }, [selectedImage]);

  useEffect(() => {
    if (!userEditedRef.current) return;
    const existing = (() => {
      try { const r = sessionStorage.getItem("reportImageData"); return r ? JSON.parse(r) : {}; } catch { return {}; }
    })();
    // selectedImagePreview intentionally omitted — never store base64 in sessionStorage
    sessionStorage.setItem("reportImageData", JSON.stringify({
      ...existing,
      dateTaken, timeTaken, latitude, longitude, landmark, noLocation,
      selectedImageId:   selectedImage?.id   ?? existing.selectedImageId,
      selectedImageName: selectedImage?.name ?? existing.selectedImageName,
      userEdited: true,
    }));
  }, [dateTaken, timeTaken, latitude, longitude, landmark, noLocation]);

  const handleOpenImageModal = () => { if (!images.length) return; setPendingImage(selectedImage); setShowImageModal(true); };
  const handleChoose = () => {
    if (!pendingImage) return;
    if (pendingImage.id !== selectedImage?.id) {
      userEditedRef.current = false;
    }
    setSelectedImage(pendingImage);
    setShowImageModal(false);
  };

  const handleMapConfirm = (lat, lng) => {
    markEdited();
    setLatitude(String(lat)); setLongitude(String(lng));
    setLatError(""); setLngError("");
    setEditingCoords(false); setShowMapModal(false);
  };

  const handleNoLocationToggle = () => {
    markEdited();
    const next = !noLocation; setNoLocation(next);
    if (next) { setLatitude(""); setLongitude(""); setLatError(""); setLngError(""); setEditingCoords(false); }
  };

  const saveCurrentState = async () => {
    // selectedImagePreview intentionally omitted — never store base64 in sessionStorage
    sessionStorage.setItem("reportImageData", JSON.stringify({
      selectedImageId:   selectedImage?.id   ?? "",
      selectedImageName: selectedImage?.name ?? "",
      dateTaken, timeTaken,
      latitude: noLocation ? "" : latitude,
      longitude: noLocation ? "" : longitude,
      landmark, noLocation,
      userEdited: userEditedRef.current,
    }));
    const imgs = await loadImages();
    const activeId = sessionStorage.getItem("activeReportId") || null;
    const report = snapshotCurrentReport(activeId, imgs || []);
    upsertReport(report);
    sessionStorage.setItem("activeReportId", report.id);
  };

  const handleBack = async () => {
    await saveCurrentState();
    navigate("/uploadFiles");
  };

  const handleNext = async () => {
    if (!selectedImage) { setSelectionError(true); return; }
    await saveCurrentState();
    const hasCoords = latitude.trim() !== "" && longitude.trim() !== "";
    const hasLandmark = landmark.trim() !== "";
    const missing = [];
    if (!dateTaken.trim()) missing.push("Date");
    if (!timeTaken.trim()) missing.push("Time");
    if (noLocation) {
      if (!hasLandmark) missing.push("Nearest town or landmark (required when not providing GPS)");
    } else {
      if (!hasCoords) missing.push("GPS coordinates (use Map or Edit, or check the opt-out box to enter a landmark instead)");
      else if (latError || lngError) missing.push("Valid GPS coordinates (fix the errors shown below the coordinate fields)");
    }
    if (missing.length > 0) {
      setValidationError(`Please fill in the following before continuing:\n• ${missing.join("\n• ")}`);
      setShowValidationPopup(true); return;
    }
    navigate("/fishInfo");
  };

  const displayDate = (v) => { if (!v) return "Not detected"; const [y,m,d] = v.split("-"); if (!y||!m||!d) return v; return new Date(Number(y),Number(m)-1,Number(d)).toLocaleDateString(); };
  const displayTime = (v) => { if (!v) return "Not detected"; const [h,min] = v.split(":"); if (!h||!min) return v; const d = new Date(); d.setHours(Number(h),Number(min)); return d.toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}); };
  const coordsDoneBlocked = editingCoords && !!(latError || lngError);

  const exifPopupIcon = exifPopupType === "success" ? faCheckCircle : faExclamationTriangle;
  const exifPopupIconClass = exifPopupType === "success" ? "ri-success-icon" : "ri-warning-icon";
  const exifPopupTitle = exifPopupType === "success" ? "Metadata Imported" : exifPopupType === "partial" ? "Partial Metadata" : "No Metadata Found";

  return (
    <>
      <div className="bg-image" />
      <div className="top-row">
        <div className="logo"><img src={logo} alt="FishTagged Logo" /></div>
        <div className="top-hamburger"><Hamburger user={user} /></div>
      </div>
      <div className="ri-viewport">
        <div className="ri-content">
          <div className="card-wrapper">
            <div className="ri-card">
              <p className="ri-step-label">New entry (3 out of 5)</p>
              <h1 className="ri-title">Report Image</h1>
              <p className="ri-subtitle">Please enter when and where you saw this fish.</p>
              <div className="ri-form-box">
                <div className="ri-field-row">
                  <span className="ri-field-icon"><FontAwesomeIcon icon={faCamera} /></span>
                  <div className="ri-field-info">
                    <span className="ri-field-label">Autofill Source</span>
                    <span className="ri-field-value">{selectedImage ? selectedImage.name : "No image selected"}</span>
                  </div>
                  <button className="ri-action-btn" onClick={handleOpenImageModal}>Choose <FontAwesomeIcon icon={faAngleRight} /></button>
                </div>
                <br />
                <p className="ri-subtitle-nomargin centered">
                  You can fill out this page automatically by importing metadata from one of your images.
                </p>
              </div>
              <br />
              <div className="ri-form-box">
                <div className="ri-field-row">
                  <span className="ri-field-icon"><FontAwesomeIcon icon={faCalendarDays} /></span>
                  <div className="ri-field-info">
                    <span className="ri-field-label">Date</span>
                    {editingDate
                      ? <input className="ri-inline-input" type="date" value={dateTaken}
                          onChange={(e) => { markEdited(); setDateTaken(e.target.value); }} />
                      : <span className="ri-field-value">{displayDate(dateTaken)}</span>}
                  </div>
                  <button className="ri-action-btn" onClick={() => { markEdited(); setEditingDate((v) => !v); }}>
                    {editingDate ? <><FontAwesomeIcon icon={faCheck} /> Done</> : <>Edit <FontAwesomeIcon icon={faAngleRight} /></>}
                  </button>
                </div>

                <div className="ri-field-row">
                  <span className="ri-field-icon"><FontAwesomeIcon icon={faClock} /></span>
                  <div className="ri-field-info">
                    <span className="ri-field-label">Time</span>
                    {editingTime
                      ? <input className="ri-inline-input" type="time" value={timeTaken}
                          onChange={(e) => { markEdited(); setTimeTaken(e.target.value); }} />
                      : <span className="ri-field-value">{displayTime(timeTaken)}</span>}
                  </div>
                  <button className="ri-action-btn" onClick={() => { markEdited(); setEditingTime((v) => !v); }}>
                    {editingTime ? <><FontAwesomeIcon icon={faCheck} /> Done</> : <>Edit <FontAwesomeIcon icon={faAngleRight} /></>}
                  </button>
                </div>

                <div className="ri-location-block">
                  <div className="ri-location-opt">
                    <input type="checkbox" id="no-location" checked={noLocation} onChange={handleNoLocationToggle} />
                    <label htmlFor="no-location">I do not wish to provide my GPS location</label>
                  </div>
                  {!noLocation && (
                    <div className="ri-location-top">
                      <span className="ri-field-icon"><FontAwesomeIcon icon={faLocationDot} /></span>
                      <div className="ri-field-info">
                        <span className="ri-field-label">GPS Coordinates</span>
                        {editingCoords ? (
                          <div className="ri-coords-edit">
                            <div className="ri-coord-field">
                              <input
                                className={`ri-inline-input ri-coord-input${latError ? " ri-input-error" : ""}`}
                                type="number" placeholder="Latitude" value={latitude}
                                onChange={(e) => { markEdited(); handleLatChange(e.target.value); }} step="0.000001"
                              />
                              {latError && <span className="ri-coord-error">{latError}</span>}
                            </div>
                            <div className="ri-coord-field">
                              <input className={`ri-inline-input ri-coord-input${lngError ? " ri-input-error" : ""}`} type="number" placeholder="Longitude" value={longitude} onChange={(e) => { markEdited(); handleLngChange(e.target.value); }} step="0.000001" />
                              {lngError && <span className="ri-coord-error">{lngError}</span>}
                            </div>
                          </div>
                        ) : (
                          <div className="ri-location-coords">
                            <span className="ri-coord-line">Latitude: {latitude || "------"}</span>
                            <span className="ri-coord-line">Longitude: {longitude || "------"}</span>
                          </div>
                        )}
                      </div>
                      <div className="ri-location-btns">
                        <button className="ri-action-btn" onClick={() => setShowMapModal(true)}>Map <FontAwesomeIcon icon={faAngleRight} /></button>
                        <button
                          className="ri-action-btn"
                          onClick={() => { if (coordsDoneBlocked) return; markEdited(); setEditingCoords((v) => !v); }}
                          style={coordsDoneBlocked ? { opacity: 0.4, cursor: "not-allowed" } : {}}
                        >
                          {editingCoords ? <><FontAwesomeIcon icon={faCheck} /> Done</> : <>Edit <FontAwesomeIcon icon={faAngleRight} /></>}
                        </button>
                      </div>
                    </div>
                  )}
                  {noLocation && (
                    <div className="ri-landmark-row">
                      <label htmlFor="landmark">Nearest town or landmark: *</label>
                      <input type="text" id="landmark" className="ri-landmark-input" value={landmark}
                        onChange={(e) => { markEdited(); setLandmark(e.target.value); }}
                        placeholder="e.g. Miami Beach Pier" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="bottom-nav">
              <FontAwesomeIcon icon={faAngleLeft} className="arrow" onClick={handleBack} />
              <FontAwesomeIcon icon={faAngleRight} className="arrow" onClick={handleNext} />
            </div>
          </div>
        </div>
      </div>

      {showImageModal && (
        <div className="ri-modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="ri-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ri-modal-close" onClick={() => setShowImageModal(false)}>✕</button>
            <h2 className="ri-modal-title">Choose an image to import data from</h2>
            <div className="ri-image-list">
              {images.map((img) => (
                <div key={img.id} className={`ri-image-item${pendingImage?.id === img.id ? " selected" : ""}`} onClick={() => setPendingImage(img)}>
                  <div className="ri-image-thumb" style={{ backgroundImage: `url(${img.preview})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                  <span className="ri-image-filename">{img.name}</span>
                </div>
              ))}
            </div>
            <div className="ri-modal-actions">
              <button className="ri-modal-cancel" onClick={() => setShowImageModal(false)}>Cancel</button>
              <button className="ri-modal-choose" onClick={handleChoose} disabled={!pendingImage}>Choose</button>
            </div>
          </div>
        </div>
      )}
      {showMapModal && <MapModal initialLat={latitude ? parseFloat(latitude) : null} initialLng={longitude ? parseFloat(longitude) : null} onConfirm={handleMapConfirm} onClose={() => setShowMapModal(false)} />}
      {exifPopup && (
        <div className="ri-modal-overlay">
          <div className="ri-modal">
            <FontAwesomeIcon icon={faTimes} className="ri-modal-close" onClick={() => setExifPopup(false)} />
            <FontAwesomeIcon icon={exifPopupIcon} className={exifPopupIconClass} />
            <h2 className="ri-modal-title">{exifPopupTitle}</h2>
            <p className="ri-modal-body" style={{ whiteSpace: 'pre-line' }}>{exifPopupMessage}</p>
            <div className="ri-modal-actions"><button className="ri-modal-choose" onClick={() => setExifPopup(false)}>OK</button></div>
          </div>
        </div>
      )}
      {showValidationPopup && (
        <div className="ri-modal-overlay">
          <div className="ri-modal">
            <FontAwesomeIcon icon={faTimes} className="ri-modal-close" onClick={() => setShowValidationPopup(false)} />
            <FontAwesomeIcon icon={faExclamationTriangle} className="ri-warning-icon" />
            <h2 className="ri-modal-title">Missing Information</h2>
            <p className="ri-modal-body" style={{ whiteSpace: 'pre-line' }}>{validationError}</p>
            <div className="ri-modal-actions"><button className="ri-modal-choose" onClick={() => setShowValidationPopup(false)}>OK</button></div>
          </div>
        </div>
      )}
      {missingImages && (
        <div className="ri-modal-overlay">
          <div className="ri-modal">
            <FontAwesomeIcon icon={faExclamationTriangle} className="ri-warning-icon" />
            <h2 className="ri-modal-title">No Images Uploaded</h2>
            <p className="ri-modal-body">You must upload at least one image before continuing.</p>
            <div className="ri-modal-actions"><button className="ri-modal-choose" onClick={() => navigate("/uploadFiles")}>Go to Upload Page</button></div>
          </div>
        </div>
      )}
      {selectionError && (
        <div className="ri-modal-overlay">
          <div className="ri-modal">
            <FontAwesomeIcon icon={faTimes} className="ri-modal-close" onClick={() => setSelectionError(false)} />
            <FontAwesomeIcon icon={faExclamationTriangle} className="ri-warning-icon" />
            <h2 className="ri-modal-title">Select an Image</h2>
            <p className="ri-modal-body">Please choose an image before continuing.</p>
            <div className="ri-modal-actions"><button className="ri-modal-choose" onClick={() => setSelectionError(false)}>OK</button></div>
          </div>
        </div>
      )}
    </>
  );
}